import React, { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';

type VerifyLabels = {
  matched: string;
  matchedDetail: string;
  mismatch: string;
  mismatchDetail: string;
};

const DEFAULT_LABELS: VerifyLabels = {
  matched: 'Equipment verified',
  matchedDetail: 'Check-out authorized',
  mismatch: 'Wrong equipment',
  mismatchDetail: 'Equipment mismatch',
};

type OverlayLine = { text: string; className: string };

/**
 * Each overlay result stacks three independently-authored strings: the caller's
 * headline label, the API's `reason`, and a fixed detail line. Any two of them
 * can be the same sentence — the field screens pass `matched: 'Check-out
 * authorized'`, which was exactly the detail line, so the overlay printed it
 * twice. Drop repeats instead of trusting callers to avoid the collision.
 */
const distinctLines = (lines: (OverlayLine | false | null | undefined)[]): OverlayLine[] => {
  const seen = new Set<string>();
  return lines.filter((line): line is OverlayLine => {
    if (!line || !line.text.trim()) return false;
    const key = line.text.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

type VerifyState =
  | { status: 'idle' }
  | { status: 'verifying' }
  | { status: 'match'; reason: string; referenceSet: boolean }
  | { status: 'no_match'; reason: string }
  | { status: 'error'; reason: string };

interface VerifyCheckOutButtonProps {
  equipmentId: string;
  hasReferencePhoto?: boolean;
  onVerified?: () => void;
  /** Override the amber button styling for the host screen. Defaults to the Equipment Detail style. */
  buttonClassName?: string;
  labels?: Partial<VerifyLabels>;
}

const DEFAULT_BUTTON_CLASS =
  'w-full bg-amber-500 hover:bg-amber-400 active:bg-amber-600 ' +
  'text-white font-semibold rounded-lg py-4 text-base ' +
  'min-h-[48px] transition-colors';

export const VerifyCheckOutButton: React.FC<VerifyCheckOutButtonProps> = ({
  equipmentId,
  hasReferencePhoto,
  onVerified,
  buttonClassName = DEFAULT_BUTTON_CLASS,
  labels,
}) => {
  const l = { ...DEFAULT_LABELS, ...labels };
  const queryClient = useQueryClient();
  const verifyFileRef = useRef<HTMLInputElement>(null);
  const [verifyState, setVerifyState] = useState<VerifyState>({ status: 'idle' });

  const handleVerifyClick = () => {
    verifyFileRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset input so selecting the same file again re-triggers onChange
    if (verifyFileRef.current) verifyFileRef.current.value = '';
    if (!file || !equipmentId) return;
    setVerifyState({ status: 'verifying' });
    try {
      const result = await api.equipment.verifyPhoto(equipmentId, file);
      if (result.match === true) {
        setVerifyState({ status: 'match', reason: result.reason, referenceSet: result.reference_set });
        onVerified?.();
        window.setTimeout(() => {
          setVerifyState({ status: 'idle' });
          if (result.reference_set) {
            queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] });
          }
        }, 3000);
      } else if (result.error) {
        setVerifyState({ status: 'error', reason: result.reason });
      } else {
        setVerifyState({ status: 'no_match', reason: result.reason });
      }
    } catch {
      setVerifyState({ status: 'error', reason: 'Could not reach verification service — please try again' });
    }
  };

  const dismissVerify = () => setVerifyState({ status: 'idle' });
  const retryVerify = () => {
    setVerifyState({ status: 'idle' });
    verifyFileRef.current?.click();
  };

  return (
    <>
      {/* Deliberately unlabelled and hidden — the visible button drives it. The
          test id is the only handle a test has on it for that reason. */}
      <input
        ref={verifyFileRef}
        type="file"
        accept="image/*"
        // Ask for the rear camera — this is a scan-the-unit-now flow, not a
        // gallery pick. A hint only: desktop ignores it and a mobile browser
        // may still offer the picker, so it cannot guarantee a fresh photo.
        capture="environment"
        onChange={handleFileSelected}
        className="hidden"
        data-testid="verify-photo-input"
      />
      <button onClick={handleVerifyClick} className={buttonClassName}>
        Verify &amp; Check Out
      </button>
      {!hasReferencePhoto && (
        <p className="text-center text-xs text-muted-foreground mt-1">
          First scan will set the reference photo
        </p>
      )}

      {/* Verification Overlay */}
      {verifyState.status !== 'idle' && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center">
          {verifyState.status === 'verifying' && (
            <div className="flex flex-col items-center gap-4 text-white">
              <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              <p className="text-lg font-medium">Verifying equipment...</p>
            </div>
          )}

          {verifyState.status === 'match' && (
            <div className="flex flex-col items-center gap-4 text-center px-6">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              {distinctLines([
                { text: l.matched, className: 'text-2xl font-bold text-green-400' },
                { text: verifyState.reason, className: 'text-sm text-white/60' },
                { text: l.matchedDetail, className: 'text-sm text-green-400/80' },
              ]).map((line) => (
                <p key={line.className} className={line.className}>{line.text}</p>
              ))}
              {verifyState.referenceSet && (
                <p className="text-sm text-amber-400">Reference photo saved for future scans</p>
              )}
              <button onClick={dismissVerify} className="mt-2 text-white/50 text-sm underline">
                Tap to dismiss
              </button>
            </div>
          )}

          {verifyState.status === 'no_match' && (
            <div className="flex flex-col items-center gap-4 text-center px-6">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-400" />
              </div>
              {distinctLines([
                { text: l.mismatch, className: 'text-2xl font-bold text-red-400' },
                { text: verifyState.reason, className: 'text-sm text-white/60' },
                { text: l.mismatchDetail, className: 'text-sm text-red-400/80' },
              ]).map((line) => (
                <p key={line.className} className={line.className}>{line.text}</p>
              ))}
              <button onClick={retryVerify} className="w-full max-w-xs bg-amber-500 text-white font-semibold rounded-lg py-3 mt-2">
                Try Again
              </button>
              <button onClick={dismissVerify} className="text-white/50 text-sm underline">
                Cancel
              </button>
            </div>
          )}

          {verifyState.status === 'error' && (
            <div className="flex flex-col items-center gap-4 text-center px-6">
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-amber-400" />
              </div>
              {distinctLines([
                { text: 'Could not verify', className: 'text-2xl font-bold text-amber-400' },
                { text: verifyState.reason, className: 'text-sm text-white/60' },
              ]).map((line) => (
                <p key={line.className} className={line.className}>{line.text}</p>
              ))}
              <button onClick={retryVerify} className="w-full max-w-xs bg-amber-500 text-white font-semibold rounded-lg py-3 mt-2">
                Try Again
              </button>
              <button onClick={dismissVerify} className="text-white/50 text-sm underline">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default VerifyCheckOutButton;
